import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Button, Space, Tag, Select, Input, message, Modal, Typography, Dropdown, DatePicker, Checkbox } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, MoreOutlined, ShoppingCartOutlined, CopyOutlined, CheckCircleOutlined, ClockCircleOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { customerAPI, customerQuoteAPI, quoteStatusAPI, productAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Customer, CustomerQuote, QuoteStatus, Product } from '../types.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';
import { QUOTE_STATUS, areQuoteRowsLocked, isQuoteTerminal, getQuoteStatusColor } from '../utils/quoteStatus.ts';
import CustomerQuoteForm from '../components/CustomerQuoteForm.tsx';
import CustomerQuoteDetailsModal from '../components/CustomerQuoteDetailsModal.tsx';
import ConvertToOrderModal from '../components/ConvertToOrderModal.tsx';
import QuoteStatusModal from '../components/QuoteStatusModal.tsx';
import AvailabilityCheckModal from '../components/AvailabilityCheckModal.tsx';

const { Option } = Select;
const { Title } = Typography;
const { RangePicker } = DatePicker;

// Preventivi Cliente: stesso schema di Gestione Ordini Cliente (filtri server-side,
// elenco completo, menu azioni, Visualizza con Cronologia, Cambia Stato con sequenza).
const CustomerQuotes: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('customer-quotes');
  const [quotes, setQuotes] = useState<CustomerQuote[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [quoteStatuses, setQuoteStatuses] = useState<QuoteStatus[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [editingQuote, setEditingQuote] = useState<CustomerQuote | null>(null);
  const [viewingQuote, setViewingQuote] = useState<CustomerQuote | null>(null);
  const [statusChangingQuote, setStatusChangingQuote] = useState<CustomerQuote | null>(null);
  const [convertingQuote, setConvertingQuote] = useState<CustomerQuote | null>(null);
  const [availabilityQuote, setAvailabilityQuote] = useState<CustomerQuote | null>(null);
  const [filters, setFilters] = useState({
    quote_number: undefined as string | undefined,
    customer_reference: undefined as string | undefined,
    customer_id: undefined as number | undefined,
    product_id: undefined as number | undefined,
    status_id: undefined as number | undefined,
    date_from: undefined as string | undefined,
    date_to: undefined as string | undefined,
    show_closed: false,
  });

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customerQuoteAPI.getCustomerQuotes({
        quote_number: filters.quote_number,
        customer_reference: filters.customer_reference,
        customer_id: filters.customer_id,
        product_id: filters.product_id,
        status_id: filters.status_id,
        date_from: filters.date_from,
        date_to: filters.date_to,
        exclude_closed: !filters.show_closed,
        limit: 0,  // elenco completo
      });
      setQuotes(data);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore nel caricamento dei preventivi');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadQuotes();
  }, [filters, loadQuotes]);

  const loadInitialData = async () => {
    try {
      const [customersData, statusesData, productsData] = await Promise.all([
        customerAPI.getCustomers(),
        quoteStatusAPI.getQuoteStatuses(),
        productAPI.getProducts(),
      ]);
      setCustomers(customersData);
      setQuoteStatuses(statusesData);
      setProducts(productsData);
    } catch (error) {
      message.error('Errore nel caricamento dei dati iniziali');
    }
  };

  const errorDetail = (error: any, fallback: string) => {
    const detail = error?.response?.data?.detail;
    return typeof detail === 'string' ? detail : fallback;
  };

  const handleCreate = () => {
    setEditingQuote(null);
    setIsFormModalVisible(true);
  };

  const handleEdit = (quote: CustomerQuote) => {
    setEditingQuote(quote);
    setIsFormModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingQuote) {
        await customerQuoteAPI.updateCustomerQuote(editingQuote.id, values);
        message.success('Preventivo aggiornato con successo');
      } else {
        await customerQuoteAPI.createCustomerQuote(values);
        message.success('Preventivo creato con successo');
      }
      setIsFormModalVisible(false);
      loadQuotes();
    } catch (error: any) {
      message.error(errorDetail(error, 'Errore nel salvataggio del preventivo'));
    }
  };

  const handleDelete = (quote: CustomerQuote) => {
    Modal.confirm({
      title: `Eliminare il preventivo ${quote.quote_number}?`,
      content: 'Il preventivo viene eliminato e il suo numero torna riutilizzabile.',
      okText: 'Sì, elimina',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await customerQuoteAPI.deleteCustomerQuote(quote.id);
          message.success('Preventivo eliminato con successo');
          loadQuotes();
        } catch (error: any) {
          message.error(errorDetail(error, "Errore nell'eliminazione del preventivo"));
        }
      },
    });
  };

  const handleStatusUpdate = async (statusData: { status_id: number; notes?: string }) => {
    if (!statusChangingQuote) return;
    try {
      const response = await customerQuoteAPI.updateQuoteStatus(statusChangingQuote.id, statusData);
      setIsStatusModalVisible(false);
      if (response?.status === 'warning') {
        message.warning(response.message);
        Modal.warning({
          title: 'Disponibilità limitata',
          content: response.message,
          onOk: () => setAvailabilityQuote({ ...statusChangingQuote, availability: response.availability_check }),
        });
      } else {
        message.success(response?.message || 'Stato preventivo aggiornato');
      }
      setStatusChangingQuote(null);
      loadQuotes();
    } catch (error: any) {
      message.error(errorDetail(error, "Errore nell'aggiornamento dello stato"));
    }
  };

  const handleCheckAvailability = async (quote: CustomerQuote) => {
    try {
      const availability = await customerQuoteAPI.checkAvailability(quote.id);
      setAvailabilityQuote({ ...quote, availability });
    } catch (error: any) {
      message.error(errorDetail(error, 'Errore nel controllo disponibilità'));
    }
  };

  const handleDuplicate = (quote: CustomerQuote) => {
    let newNumber = `${quote.quote_number}-COPIA`.toUpperCase().slice(0, 50);
    Modal.confirm({
      title: `Duplica il preventivo ${quote.quote_number}`,
      content: (
        <div>
          <p>Il nuovo preventivo nasce in stato Bozza con le stesse righe e la stessa testata.</p>
          <Input
            defaultValue={newNumber}
            maxLength={50}
            style={{ textTransform: 'uppercase' }}
            placeholder="Numero del nuovo preventivo"
            onChange={(e) => { newNumber = e.target.value.toUpperCase(); }}
          />
        </div>
      ),
      okText: 'Duplica',
      cancelText: 'Annulla',
      onOk: async () => {
        try {
          await customerQuoteAPI.duplicateQuote(quote.id, {
            quote_number: newNumber,
            quote_date: dayjs().format('YYYY-MM-DD'),
            expiry_date: dayjs().add(30, 'day').format('YYYY-MM-DD'),
          });
          message.success('Preventivo duplicato con successo');
          loadQuotes();
        } catch (error: any) {
          message.error(errorDetail(error, 'Errore nella duplicazione del preventivo'));
          throw error;
        }
      },
    });
  };

  const handleDateRangeChange = (_dates: any, dateStrings: string[]) => {
    setFilters({ ...filters, date_from: dateStrings[0] || undefined, date_to: dateStrings[1] || undefined });
  };

  const getActionItems = (record: CustomerQuote): MenuProps['items'] => {
    const rowsLocked = areQuoteRowsLocked(record.quote_status_id);
    const terminal = isQuoteTerminal(record.quote_status_id);
    const converted = !!record.converted_order;
    const items: MenuProps['items'] = [
      { key: 'view', icon: <EyeOutlined />, label: 'Visualizza', onClick: () => setViewingQuote(record) },
    ];
    if (canEdit) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: rowsLocked ? 'Modifica (solo testata)' : 'Modifica',
        disabled: terminal,
        onClick: () => handleEdit(record),
      });
      items.push({
        key: 'status',
        icon: <CheckCircleOutlined />,
        label: 'Cambia Stato',
        disabled: terminal,
        onClick: () => { setStatusChangingQuote(record); setIsStatusModalVisible(true); },
      });
    }
    items.push({ key: 'availability', icon: <SearchOutlined />, label: 'Controlla Disponibilità', onClick: () => handleCheckAvailability(record) });
    if (canEdit) {
      items.push({ key: 'duplicate', icon: <CopyOutlined />, label: 'Duplica', onClick: () => handleDuplicate(record) });
      if (record.quote_status_id === QUOTE_STATUS.ACCEPTED && !converted) {
        items.push({
          key: 'convert',
          icon: <ShoppingCartOutlined />,
          label: 'Converti in Ordine',
          onClick: () => { setConvertingQuote(record); setIsConvertModalVisible(true); },
        });
      }
      items.push({ type: 'divider' });
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Elimina',
        danger: true,
        disabled: converted,
        onClick: () => handleDelete(record),
      });
    }
    return items;
  };

  const renderExpiry = (record: CustomerQuote) => {
    if (isQuoteTerminal(record.quote_status_id)) return '-';
    if (!record.expiry_date) return '-';
    const days = Math.ceil((new Date(record.expiry_date).getTime() - new Date().setHours(0, 0, 0, 0)) / (1000 * 3600 * 24));
    if (days < 0) return <Tag color="red" icon={<ClockCircleOutlined />}>Scaduta</Tag>;
    if (days <= 7) return <Tag color="orange" icon={<ClockCircleOutlined />}>In scadenza ({days} gg)</Tag>;
    return <Tag color="green">Valida</Tag>;
  };

  const columns = [
    { title: 'Numero Preventivo', dataIndex: 'quote_number', key: 'quote_number', render: (v: string) => <strong>{v}</strong> },
    { title: 'Cliente', dataIndex: ['customer', 'company'], key: 'customer' },
    { title: 'Rif. Cliente', dataIndex: 'customer_reference', key: 'customer_reference', render: (v: string) => v || '-' },
    { title: 'Data', dataIndex: 'quote_date', key: 'quote_date', render: (d: string) => new Date(d).toLocaleDateString() },
    { title: 'Scadenza', key: 'expiry', render: (record: CustomerQuote) => (
      <Space size={4}>
        {record.expiry_date ? new Date(record.expiry_date).toLocaleDateString() : '-'}
        {renderExpiry(record)}
      </Space>
    ) },
    { title: 'Consegna Richiesta', dataIndex: 'requested_delivery_date', key: 'requested_delivery_date', render: (d: string) => d ? new Date(d).toLocaleDateString() : '-' },
    { title: 'Stato', key: 'status', render: (record: CustomerQuote) => (
      <Tag color={getQuoteStatusColor(record.quote_status)}>{record.quote_status?.display_name || 'N/A'}</Tag>
    ) },
    { title: 'Ordine Stock', dataIndex: 'reorder', key: 'reorder', render: (reorder: boolean) => (
      reorder ? <Tag color="orange" icon={<ReloadOutlined />}>ORDINE STOCK</Tag> : <span style={{ color: '#999' }}>-</span>
    ) },
    { title: 'Importo Totale', dataIndex: 'total_amount', key: 'total_amount', render: (amount: number) => amount ? `€ ${formatCurrency(amount)}` : '-' },
    { title: 'Ordine Generato', key: 'converted_order', render: (record: CustomerQuote) => (
      record.converted_order ? <Tag color="green">{record.converted_order.order_number}</Tag> : '-'
    ) },
    { title: 'Azioni', key: 'actions', render: (_: any, record: CustomerQuote) => (
      <Dropdown menu={{ items: getActionItems(record) }} trigger={['click']}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    ) },
  ];

  const editingLocked = !!editingQuote && areQuoteRowsLocked(editingQuote.quote_status_id);

  return (
    <div>
      <Title level={2}>Preventivi Cliente</Title>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="Filtra per Numero Preventivo"
              style={{ width: 200 }}
              value={filters.quote_number}
              onChange={(e) => setFilters({ ...filters, quote_number: e.target.value || undefined })}
              allowClear
            />
            <Input
              placeholder="Filtra per Rif. Cliente"
              style={{ width: 180 }}
              value={filters.customer_reference}
              onChange={(e) => setFilters({ ...filters, customer_reference: e.target.value || undefined })}
              allowClear
            />
            <Select
              placeholder="Filtra per Cliente"
              style={{ width: 200 }}
              value={filters.customer_id}
              onChange={(value) => setFilters({ ...filters, customer_id: value })}
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {customers.map((customer) => (
                <Option key={customer.id} value={customer.id}>{customer.company}</Option>
              ))}
            </Select>
            <Select
              placeholder="Filtra per Prodotto"
              style={{ width: 280 }}
              value={filters.product_id}
              onChange={(value) => setFilters({ ...filters, product_id: value })}
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {products.map((product) => (
                <Option key={product.id} value={product.id}>
                  {generateProductDisplayName(product.internal_product_code, product.size, product.color, product.product_description)}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Filtra per Stato"
              style={{ width: 150 }}
              value={filters.status_id}
              onChange={(value) => setFilters({ ...filters, status_id: value })}
              allowClear
            >
              {quoteStatuses.map((status) => (
                <Option key={status.id} value={status.id}>{status.display_name}</Option>
              ))}
            </Select>
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={['Data Da', 'Data A']}
              onChange={handleDateRangeChange}
              value={[filters.date_from ? dayjs(filters.date_from) : null, filters.date_to ? dayjs(filters.date_to) : null]}
            />
            <Checkbox checked={filters.show_closed} onChange={(e) => setFilters({ ...filters, show_closed: e.target.checked })}>
              Visualizza preventivi chiusi
            </Checkbox>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Nuovo Preventivo
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={quotes}
          rowKey="id"
          loading={loading}
          pagination={{ total: quotes.length, pageSize: 10, showSizeChanger: true, showQuickJumper: true }}
          scroll={{ x: true }}
        />

        <Modal
          title={editingQuote ? (editingLocked ? 'Modifica Preventivo (solo testata)' : 'Modifica Preventivo') : 'Nuovo Preventivo'}
          open={isFormModalVisible}
          onCancel={() => setIsFormModalVisible(false)}
          footer={null}
          width="90%"
          destroyOnClose
        >
          <CustomerQuoteForm
            initialValues={editingQuote}
            headerOnly={editingLocked}
            customers={customers}
            quoteStatuses={quoteStatuses}
            onSubmit={handleSubmit}
            onCancel={() => setIsFormModalVisible(false)}
          />
        </Modal>

        <Modal
          title="Cambia Stato Preventivo"
          open={isStatusModalVisible}
          onCancel={() => setIsStatusModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <QuoteStatusModal
            quote={statusChangingQuote}
            quoteType="customer"
            quoteStatuses={quoteStatuses}
            onSubmit={handleStatusUpdate}
            onCancel={() => setIsStatusModalVisible(false)}
          />
        </Modal>

        {viewingQuote && (
          <CustomerQuoteDetailsModal quote={viewingQuote} onClose={() => setViewingQuote(null)} />
        )}

        <ConvertToOrderModal
          visible={isConvertModalVisible}
          quote={convertingQuote}
          onClose={() => { setIsConvertModalVisible(false); setConvertingQuote(null); }}
          onConverted={loadQuotes}
        />

        <Modal
          title="Controllo Disponibilità"
          open={availabilityQuote !== null}
          onCancel={() => setAvailabilityQuote(null)}
          footer={null}
          width={1000}
          destroyOnClose
        >
          <AvailabilityCheckModal quote={availabilityQuote} onClose={() => setAvailabilityQuote(null)} />
        </Modal>
      </Card>
    </div>
  );
};

export default CustomerQuotes;
